<template>
  <div>
    <h3 v-if="!submited">{{ $t('organization.slogan') }}
      <small>
        <checkbox v-model="showForm">
          <b slot="label" class="uppercase" style="font-size: 25px">{{ $t('yes') }}</b>
        </checkbox>
        <p v-show="showForm">{{ $t('organization.fillFields') }}</p>
      </small>
    </h3>
    <h3 v-else>{{ $t('organization.yourOrganization') }}:
      <br><br>
      <small v-if="organization.status === 'pending'">{{ $t('organization.submitted') }}</small>
      <small v-if="organization.status === 'approved'">{{ $t('organization.approved') }}</small>
      <small>{{ $t('organization.contactAdmin') }}</small>
    </h3>
    <div v-show="showForm">
    <form-error :errors='errors' :attribute="'name'">
      <input v-model="organization.name" class="form-control" :disabled="submited" :placeholder="$t('organization.name')">
    </form-error>
    <div class="row">
      <div class="col-md-5">
        <form-error :errors='errors' :attribute="'address'">
          <input v-model="organization.address_line_1" class="form-control" :disabled="submited" :placeholder="$t('organization.streetName')">
        </form-error>
      </div>
      <div class="col-md-4">
        <form-error :errors='errors' :attribute="'address'">
          <input v-model="organization.address_line_2" class="form-control" :disabled="submited" :placeholder="$t('organization.streetNumber')">
        </form-error>
      </div>
      <div class="col-md-3">
        <form-error :errors='errors' :attribute="'postcode'">
          <input v-model="organization.postcode" :disabled="submited" class="form-control" :placeholder="$t('organization.zipCode')">
        </form-error>
      </div><!-- .col-md-2 -->
    </div>
    <div class="row">
      <div class="col-md-4">
        <form-error :errors='errors' :attribute="'nip'">
          <input v-model="organization.nip" class="form-control" :disabled="submited" :placeholder="$t('organization.vat')">
        </form-error>
      </div><!-- .col-md-2 -->
      <div class="col-md-4">
        <form-error :errors='errors' :attribute="'city'">
          <input v-model="organization.city" class="form-control" :disabled="submited" :placeholder="$t('organization.city')">
        </form-error>
      </div><!-- .col-md-4 -->
      <div class="col-md-4">
        <form-error :errors='errors' :attribute="'country'">
          <select v-if="countries.length" v-model="organization.country_id" :disabled="submited" class="form-control">
            <option disabled value="">{{ $t('country') }}</option>
            <option v-for="(country, index) in countries" :value="country.id" :key="index">{{ country.name }}</option>
          </select>
        </form-error>
      </div><!-- .col-md-4 -->
      <div class="col-md-12">
        <form-error :errors='errors' :attribute="'iban'">
          <input v-model="organization.iban" class="form-control" :disabled="submited" :placeholder="$t('iban')">
        </form-error>
      </div><!-- .col-md-4 -->
    </div>
    <p>{{ $t("organization.vatInfo") }}
    <a href="https://europa.eu/youreurope/business/vat-customs/check-number-vies/index_pl.htm" target="blank">{{ $t("here") }}</a></p>
    <button type="button" v-if="!organization.id" @click.prevent="saveOrganization" class="btn btn-default btn-lg button-wide-crowd">{{ $t("save") }}</button>
    <div v-if="created">
      <button type="button" @click.prevent="updateOrganization" class="btn btn-default btn-lg button-wide-crowd">{{ $t("update") }}</button>
      <p>{{ $t("organization.editNote") }}</p>
      <button type="button" @click.prevent="submitOrganization" class="btn btn-default btn-lg button-wide-crowd">{{ $t("profile.edit.confirmOrganization") }}</button>
    </div>
    <alert :success="alert.success" :error="alert.error"></alert>
  </div>

  <div class="row">
    <div v-if="approved" class="col-md-12">
      <h3>{{ $t("profile.edit.yourDocuments") }}:</h3>
      <button type="button" @click="openModal" class="btn btn-default btn-lg button-wide-crowd">
        {{ $t('profile.kyc.createMangopayProfile') }}
      </button>
    </div>
  </div>


    <!-- Form Modal -->
    <transition v-if="formModalOpen" name="modal">
       <Form :user="user" :organization="organization" @closeFormModal="closeFormModal" @openDocumentsForm="openDocumentsForm"></Form>
    </transition>

    <!-- Documents Modal -->
    <transition v-if="documentsModalOpen" name="documentsModal">
      <modal :close="closeDocumentsModal" width="80%">
        <div slot="header">
          <h3>{{ $t('profile.kyc.addDocuments') }}</h3>
        </div>
        <div slot="body">
          <Documents :organization="organization" @document:created="setDocumentId"></Documents>
        </div>
        <button type="button" slot="footer" class="btn btn-primary" style="margin: 5px"
          @click="formModalOpen = true; documentsModalOpen = false">
          {{ $t('profile.kyc.editOrganizationData') }}
        </button>
      </modal>
    </transition>

  </div>
</template>

<script>
// API
import dictionariesApi from '@/api/v1/shared/dictionaries';
import userApi from '@/api/v1/user';
import organizationApi from '@/api/v1/organizations';
// Components
import Alert from '@/components/Alert';
import FormError from '@/components/FormError';
import UploadBase64 from '@/components/UploadBase64';
import Modal from '@/components/Modal';
import Documents from '@/components/Organization/Documents';
import Form from '@/components/Organization/Form';
import Checkbox from '@/components/Utils/Inputs/Checkbox';
// Mixins
import alert from '@/mixins/alert';

export default {
  components: { Alert, FormError, UploadBase64, Modal, Documents, Form, Checkbox },
  mixins: [alert],
  props: ['user'],
  data() {
    return {
      countries: [],
      showForm: false,
      organization: {
        country_id: '',
        status: '',
      },
      formModalOpen: false,
      documentsModalOpen: false,
      documents: [],
      documentsSubmitted: false,
      documentsStatus: {},
    };
  },
  computed: {
    created() {
      return this.organization.status === 'created';
    },
    submited() {
      return this.organization.status === 'pending' || this.organization.status === 'approved';
    },
    approved() {
      return this.organization.status === 'approved';
    },
    allDocumentsCreated() {
      const o = this.organization;
      return o.identity_proof_id && o.articles_of_association_id &&
              o.registration_proof_id && o.shareholder_declaration_id;
    },
  },
  methods: {
    saveOrganization() {
      this.clearAlerts();
      organizationApi.new(this.organization).then((response) => {
        this.organization = response.data;
        this.alert.success = 'Organizacja została zapisana pomyślnie';
      }).catch((response) => {
        this.errors = response.response.data;
      });
    },
    openModal() {
      if (this.organization.mangopay_id) {
        this.documentsModalOpen = true;
      } else {
        this.formModalOpen = true;
      }
    },
    openDocumentsForm() {
      this.formModalOpen = false;
      this.documentsModalOpen = true;
    },
    closeFormModal() {
      this.formModalOpen = false;
    },
    closeDocumentsModal() {
      this.documentsModalOpen = false;
    },
    updateOrganization() {
      this.clearAlerts();
      organizationApi.edit(this.organization).then((response) => {
        this.organization = response.data;
        this.alert.success = this.$t('organization.successAlert');
      }).catch((response) => {
        this.errors = response.response.data;
      });
    },
    submitOrganization() {
      this.clearAlerts();
      organizationApi.submit(this.organization.id).then((response) => {
        this.organization = response.data;
        this.alert.success = this.$t('organization.submitAlert');
      });
    },
    setDocumentId(document) {
      this.organization[`${document.Type.toLowerCase()}_id`] = document.Id;
    },
  },
  created() {
    dictionariesApi.getCountries((countries) => { this.countries = countries; });
    userApi.getOrganization((organization) => {
      if (organization) {
        this.organization = organization;
        this.showForm = true;
      }
    });
    if (this.$route.hash === '#organization') {
      this.showForm = true;
    }
  },
};
</script>



// WEBPACK FOOTER //
// Edit.vue?e9a3c826